/* PRIVA — Open Questions & Clarifications + Risks & Assumptions sections.
 *
 * Lives in its own file because (a) the parent RfpPrivaIntelligence.jsx is
 * already ~1200 lines, and (b) the categorisation / prioritisation /
 * "why it matters" logic is meaningful enough on its own to deserve a
 * dedicated module — it's the spec'd "first-class consultant review"
 * section.
 *
 * Future-ready: the data shape produced here (category, priority, reason,
 * source) is the same one we'll feed into workshop trackers / customer
 * questionnaires / Excel exports later — no rework needed when those
 * features land.
 */
import { useMemo, useState } from "react";
import {
  HelpCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  LightbulbIcon,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

function asString(v) {
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (v && typeof v === "object") {
    return (
      v.question ||
      v.text ||
      v.description ||
      v.label ||
      v.title ||
      v.detail ||
      v.summary ||
      ""
    ).toString();
  }
  return "";
}

// Walk the parsed extraction tree and pull every `open_questions` /
// `missing_areas` / `clarifications` / `unknowns` array we find. We do this
// instead of hard-coding a single path because the active admin prompt can
// (and does) place these arrays at multiple levels.
function collectQuestions(result) {
  if (!result || typeof result !== "object") return [];
  const buckets = [];
  const visit = (node, path) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach((item, i) => visit(item, `${path}[${i}]`));
      return;
    }
    if (typeof node !== "object") return;
    for (const [k, v] of Object.entries(node)) {
      const lk = k.toLowerCase();
      if (
        ["open_questions", "clarifications", "unknowns"].includes(lk) &&
        Array.isArray(v)
      ) {
        v.forEach((item) =>
          buckets.push({ text: asString(item), source: path || "root", origin: lk })
        );
      } else if (lk === "missing_areas" && Array.isArray(v)) {
        v.forEach((item) =>
          buckets.push({
            text: asString(item),
            source: path || "root",
            origin: "missing_areas",
          })
        );
      } else {
        visit(v, `${path}.${k}`);
      }
    }
  };
  visit(result, "");

  // Dedupe by normalised text (case-insensitive, whitespace-collapsed).
  const seen = new Set();
  return buckets
    .map((b) => ({ ...b, text: b.text.trim() }))
    .filter((b) => {
      if (!b.text) return false;
      const norm = b.text.toLowerCase().replace(/\s+/g, " ");
      if (seen.has(norm)) return false;
      seen.add(norm);
      return true;
    });
}

// Pull risks + assumptions from anywhere they appear in the extraction.
// When a matched key's value is itself a nested object (e.g.
// complexity_signals.multi_site_complexity = { detected: true, count: 2 }),
// we walk that subtree and humanise its leaf strings into bullets so the
// consultant sees something useful instead of an empty card.
function collectByKeys(result, keys) {
  if (!result || typeof result !== "object") return [];
  const out = [];
  const targets = new Set(keys.map((k) => k.toLowerCase()));

  const humaniseLeaf = (k, v) => {
    const label = k
      .toString()
      .replace(/[_-]+/g, " ")
      .trim()
      .replace(/\b([a-z])/g, (_m, c) => c.toUpperCase());
    if (typeof v === "boolean") {
      // Only surface positive (true) signals — false-flags add noise.
      return v ? label : null;
    }
    if (typeof v === "number") return `${label}: ${v}`;
    if (typeof v === "string") {
      return v.trim() ? `${label}: ${v.trim()}` : null;
    }
    return null;
  };

  const harvest = (subtree, path = "") => {
    if (subtree === null || subtree === undefined) return;
    if (typeof subtree === "string") {
      const t = subtree.trim();
      if (t) out.push(t);
      return;
    }
    if (Array.isArray(subtree)) {
      subtree.forEach((x) => {
        if (typeof x === "string" && x.trim()) out.push(x.trim());
        else if (x && typeof x === "object") harvest(x, path);
      });
      return;
    }
    if (typeof subtree !== "object") return;
    for (const [k, v] of Object.entries(subtree)) {
      if (v === null || v === undefined || v === "") continue;
      if (Array.isArray(v) || typeof v === "object") {
        // Recurse — but also try to humanise a top-level "{ label: true }"
        // shape as a bullet so we don't lose simple flags.
        if (!Array.isArray(v) && typeof v === "object") {
          const inner = Object.entries(v).filter(
            ([, iv]) => iv !== null && iv !== undefined && iv !== ""
          );
          const allLeaves = inner.every(
            ([, iv]) => typeof iv !== "object" || Array.isArray(iv) === false
          );
          if (allLeaves && inner.length <= 4) {
            const parts = inner
              .map(([ik, iv]) => humaniseLeaf(ik, iv))
              .filter(Boolean);
            if (parts.length > 0) {
              const headline = k
                .replace(/[_-]+/g, " ")
                .trim()
                .replace(/\b([a-z])/g, (_m, c) => c.toUpperCase());
              out.push(`${headline}: ${parts.join(", ")}`);
              continue;
            }
          }
        }
        harvest(v, `${path}.${k}`);
        continue;
      }
      // primitive leaf — only useful when its key was already matched as a
      // risk/assumption bucket; otherwise skip.
      if (typeof v === "string" && v.trim()) out.push(v.trim());
    }
  };

  const visit = (node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (typeof node !== "object") return;
    for (const [k, v] of Object.entries(node)) {
      if (targets.has(k.toLowerCase())) {
        harvest(v);
      }
      visit(v);
    }
  };
  visit(result);

  // Dedup + cap at a reasonable list size so the panel stays scannable.
  const seen = new Set();
  return out
    .map((s) => s.toString().trim())
    .filter(Boolean)
    .filter((t) => {
      const n = t.toLowerCase().replace(/\s+/g, " ");
      if (seen.has(n)) return false;
      seen.add(n);
      return true;
    })
    .slice(0, 25);
}

/* -------------------------------------------------------------------------- */
/*  Categorisation + priority heuristics                                       */
/* -------------------------------------------------------------------------- */

// (regex, category) tuples — first match wins. Patterns use plural-tolerant
// forms (`\bskus?\b`, `\bvolumes?\b`, …) so natural language phrasing in
// extracted questions doesn't silently fall into General Clarification.
const CATEGORY_RULES = [
  [/\b(sap|ewm|s\/?4|s\/4hana|ecc|wm\b|wms)\b/i, "SAP Landscape"],
  [/\b(edi|interfaces?|integrations?|middleware|apis?|iflow|cpi|po\/po)\b/i, "Integration"],
  [/\b(automations?|conveyors?|robots?|agvs?|as\/rs|sortation|cranes?|mhe|pick.to.light|put.to.light)\b/i, "Warehouse Automation"],
  [/\b(volumes?|throughputs?|kpis?|skus?|lines?\s+per|capacities|capacity|orders?\s+per|movements?\s+per)\b/i, "Volumes & KPIs"],
  [/\b(\brfs?\b|hhts?|handhelds?|mobile|offline|wifi|signal|voice\s+pick)\b/i, "Mobility / RF"],
  [/\b(infrastructure|cloud|on.?prem|host(ing)?|deployments?|tenants?|environments?)\b/i, "Infrastructure"],
  [/\b(security|compliance|gdpr|audits?|access\s+control|sox|hipaa|encryption|sso)\b/i, "Security & Compliance"],
  [/\b(reports?(ing)?|analytics?|dashboards?|bi\b)\b/i, "Reporting & Analytics"],
  [/\b(migrations?|legacy|cutover|historical\s+data|data\s+loads?)\b/i, "Data Migration"],
  [/\b(in\s+scope|out\s+of\s+scope|scope|boundary|inclusions?|exclusions?)\b/i, "Scope Clarification"],
  [/\b(shifts?|crew|warehouse\s+staff|workforce|headcount|operators?)\b/i, "Operations"],
  [/\b(process(es)?|workflows?|operations?|procedures?)\b/i, "Business Process"],
];

function categorize(text) {
  const t = (text || "").toString();
  for (const [re, cat] of CATEGORY_RULES) {
    if (re.test(t)) return cat;
  }
  return "General Clarification";
}

// Default priority by category (HIGH is the spec'd default for the
// "expensive-to-get-wrong" topics — SAP landscape, integration, automation,
// volumes, infrastructure, data migration).
const PRIORITY_BY_CATEGORY = {
  "SAP Landscape": "High",
  Integration: "High",
  "Warehouse Automation": "High",
  "Volumes & KPIs": "High",
  Infrastructure: "High",
  "Data Migration": "High",
  "Scope Clarification": "Medium",
  "Mobility / RF": "Medium",
  "Security & Compliance": "Medium",
  "Reporting & Analytics": "Medium",
  Operations: "Medium",
  "Business Process": "Medium",
  "General Clarification": "Low",
};

// Spotter overrides — these phrases bump priority regardless of category.
const HIGH_PRIORITY_BUMPERS = [
  /missing\s+(warehouse\s+count|site\s+count|integration\s+architecture)/i,
  /unknown\s+(volume|throughput|sku)/i,
  /unclear\s+(sap|ewm|landscape)/i,
  /\bgo.?live\b/i,
  /\bcritical\b/i,
  /transaction\s+volume/i,
];

function prioritize(text, category) {
  const t = (text || "").toString();
  if (HIGH_PRIORITY_BUMPERS.some((re) => re.test(t))) return "High";
  return PRIORITY_BY_CATEGORY[category] || "Medium";
}

// Canonical, consultant-friendly reasons. Short and outcome-focused.
const WHY_IT_MATTERS = {
  "SAP Landscape":
    "Affects EWM edition selection, deployment model, and integration architecture.",
  Integration: "Impacts integration estimation and middleware scope.",
  "Warehouse Automation":
    "Drives EWM-MFS and automation interface scope.",
  "Volumes & KPIs":
    "Required for warehouse throughput estimation and infrastructure sizing.",
  "Mobility / RF": "Required for sizing RF infrastructure complexity.",
  Infrastructure:
    "Affects deployment model, hosting, and licensing decisions.",
  "Security & Compliance":
    "Affects compliance scope and authorization design.",
  "Reporting & Analytics":
    "Drives analytics solution scoping and reporting stack.",
  "Data Migration":
    "Affects migration plan, cutover strategy, and historical data scope.",
  "Scope Clarification":
    "Required to lock the SoW boundary before estimation.",
  Operations:
    "Required to scope operations workshops and staffing model.",
  "Business Process":
    "Impacts process workshop scope and configuration effort.",
  "General Clarification":
    "Required before SmartEvaluate conversion and effort sizing.",
};

/* -------------------------------------------------------------------------- */
/*  Visual primitives                                                          */
/* -------------------------------------------------------------------------- */

const PRIORITY_STYLES = {
  High: "bg-[#FEE2E2] text-[#991B1B] border-[#991B1B]/15",
  Medium: "bg-[#FEF3C7] text-[#92400E] border-[#92400E]/15",
  Low: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
};

function PriorityBadge({ value }) {
  return (
    <span
      data-testid={`rfp-priva-question-priority-${(value || "low").toLowerCase()}`}
      className={[
        "inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 border",
        PRIORITY_STYLES[value] || PRIORITY_STYLES.Low,
      ].join(" ")}
    >
      {value} Priority
    </span>
  );
}

function CategoryBadge({ value }) {
  return (
    <span
      data-testid={`rfp-priva-question-category-${value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 bg-[#E0F2FE] text-[#025979] border border-[#025979]/15"
    >
      {value}
    </span>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, count, testId, open, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className="w-full flex items-start justify-between gap-3 px-5 py-3.5 sm:px-6 hover:bg-[#F8FAFC] transition-colors text-left"
    >
      <div className="flex items-start gap-2.5 min-w-0">
        <div className="w-8 h-8 rounded-sm bg-[#E0F2FE] flex items-center justify-center shrink-0 mt-0.5">
          <Icon className="w-4 h-4 text-[#025979]" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4
              className="text-[13px] font-semibold text-[#111827] uppercase tracking-wider"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {title}
            </h4>
            {typeof count === "number" && count > 0 && (
              <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB]">
                {count}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="mt-1 text-[12px] text-[#4B5563] leading-relaxed max-w-3xl">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {open ? (
        <ChevronDown className="w-4 h-4 text-[#9CA3AF] mt-1 shrink-0" />
      ) : (
        <ChevronRight className="w-4 h-4 text-[#9CA3AF] mt-1 shrink-0" />
      )}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  OpenQuestionsSection                                                       */
/* -------------------------------------------------------------------------- */

const PRIORITY_ORDER = { High: 0, Medium: 1, Low: 2 };

function QuestionRow({ question }) {
  const isHigh = question.priority === "High";
  return (
    <div
      data-testid={`rfp-priva-question-row-${question.id}`}
      className={[
        "border rounded-sm px-4 py-3 transition-colors",
        isHigh
          ? "bg-[#FEF2F2] border-[#FCA5A5]/60"
          : "bg-white border-[#E5E7EB] hover:bg-[#F8FAFC]",
      ].join(" ")}
    >
      <p className="text-sm text-[#111827] leading-relaxed font-medium">
        {question.text}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <CategoryBadge value={question.category} />
        <PriorityBadge value={question.priority} />
        {question.origin === "missing_areas" && (
          <span
            data-testid="rfp-priva-question-missing-area"
            className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] border border-[#92400E]/15"
          >
            Missing Area
          </span>
        )}
      </div>
      <p className="mt-2 text-[12px] text-[#4B5563] leading-relaxed">
        <span className="font-semibold text-[#374151]">Why it matters:</span>{" "}
        {question.reason}
      </p>
    </div>
  );
}

function CategoryGroup({ category, items }) {
  const [open, setOpen] = useState(true);
  return (
    <div
      data-testid={`rfp-priva-question-group-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#F1F5F9] transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="w-3.5 h-3.5 text-[#9CA3AF]" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF]" />
          )}
          <span className="text-[12px] font-semibold text-[#111827] uppercase tracking-wider">
            {category}
          </span>
          <span className="text-[11px] font-medium text-[#6B7280]">
            ({items.length})
          </span>
        </div>
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2 border-t border-[#E5E7EB]">
          {items.map((q) => (
            <QuestionRow key={q.id} question={q} />
          ))}
        </div>
      )}
    </div>
  );
}

export function OpenQuestionsSection({ result }) {
  const [open, setOpen] = useState(true);

  const enriched = useMemo(() => {
    const collected = collectQuestions(result);
    return collected.map((q, i) => {
      const category = categorize(q.text);
      const priority = prioritize(q.text, category);
      return {
        id: `oq_${i}`,
        text: q.text,
        source: q.source,
        origin: q.origin,
        category,
        priority,
        reason:
          WHY_IT_MATTERS[category] || WHY_IT_MATTERS["General Clarification"],
      };
    });
  }, [result]);

  const grouped = useMemo(() => {
    const buckets = new Map();
    enriched.forEach((q) => {
      if (!buckets.has(q.category)) buckets.set(q.category, []);
      buckets.get(q.category).push(q);
    });
    // Sort items within each bucket by priority, then by source-derived order.
    for (const list of buckets.values()) {
      list.sort(
        (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
      );
    }
    // Order categories by highest-priority item, then by count.
    const groups = Array.from(buckets.entries()).map(([cat, items]) => ({
      category: cat,
      items,
      maxPriority: Math.min(...items.map((x) => PRIORITY_ORDER[x.priority])),
    }));
    groups.sort(
      (a, b) =>
        a.maxPriority - b.maxPriority ||
        b.items.length - a.items.length ||
        a.category.localeCompare(b.category)
    );
    return groups;
  }, [enriched]);

  const counts = useMemo(() => {
    const c = { High: 0, Medium: 0, Low: 0 };
    enriched.forEach((q) => {
      c[q.priority] = (c[q.priority] || 0) + 1;
    });
    return c;
  }, [enriched]);

  return (
    <section
      data-testid="rfp-priva-open-questions-section"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <SectionHeader
        testId="rfp-priva-open-questions-toggle"
        icon={HelpCircle}
        title="Open Questions & Clarifications"
        subtitle="Areas requiring customer clarification before final SmartEvaluate conversion and transformation analysis."
        count={enriched.length}
        open={open}
        onToggle={() => setOpen((o) => !o)}
      />
      {open && (
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-[#E5E7EB]">
          {enriched.length === 0 ? (
            <p
              data-testid="rfp-priva-open-questions-empty"
              className="text-sm text-[#4B5563] leading-relaxed"
            >
              No major clarification gaps detected from uploaded RFP documents.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {counts.High > 0 && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-sm px-2 py-1 bg-[#FEE2E2] text-[#991B1B] border border-[#991B1B]/15">
                    <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                    {counts.High} High
                  </span>
                )}
                {counts.Medium > 0 && (
                  <span className="inline-flex items-center text-[11px] font-semibold rounded-sm px-2 py-1 bg-[#FEF3C7] text-[#92400E] border border-[#92400E]/15">
                    {counts.Medium} Medium
                  </span>
                )}
                {counts.Low > 0 && (
                  <span className="inline-flex items-center text-[11px] font-semibold rounded-sm px-2 py-1 bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB]">
                    {counts.Low} Low
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {grouped.map((g) => (
                  <CategoryGroup
                    key={g.category}
                    category={g.category}
                    items={g.items}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  RisksAssumptionsSection                                                    */
/* -------------------------------------------------------------------------- */

const RISK_KEYS = [
  "risks",
  "risk_factors",
  "complexity_signals",
  "key_complexity_signals",
  "inventory_control_signals",
  "delivery_risks",
  // Nested signal buckets emitted by the active SmartEvaluate RFP prompt —
  // these are consultant-level risk indicators (custom dev needed, complex
  // integrations, multi-site, automation, etc.).
  "integration_complexity_signals",
  "multi_site_complexity_signals",
  "custom_development_signals",
  "automation_signals",
];

const ASSUMPTION_KEYS = [
  "assumptions",
  "design_assumptions",
  "sizing_assumptions",
  "extraction_assumptions",
];

export function RisksAssumptionsSection({ result }) {
  const [open, setOpen] = useState(true);
  const risks = useMemo(() => collectByKeys(result, RISK_KEYS), [result]);
  const assumptions = useMemo(
    () => collectByKeys(result, ASSUMPTION_KEYS),
    [result]
  );

  // Hide entirely if there's nothing on either side — the parent panel is
  // already information-dense, no point showing an empty card.
  if (risks.length === 0 && assumptions.length === 0) return null;

  return (
    <section
      data-testid="rfp-priva-risks-assumptions-section"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <SectionHeader
        testId="rfp-priva-risks-assumptions-toggle"
        icon={ShieldAlert}
        title="Risks & Assumptions"
        subtitle="Initial risk signals and assumptions detected from the RFP — to be confirmed during clarification workshops."
        count={risks.length + assumptions.length}
        open={open}
        onToggle={() => setOpen((o) => !o)}
      />
      {open && (
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-[#E5E7EB]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <RiskOrAssumptionList
              testId="rfp-priva-risks-list"
              icon={AlertTriangle}
              accent="text-[#991B1B]"
              title="Risks"
              items={risks}
              emptyLabel="No explicit risks detected — confirm during workshops."
            />
            <RiskOrAssumptionList
              testId="rfp-priva-assumptions-list"
              icon={LightbulbIcon}
              accent="text-[#92400E]"
              title="Assumptions"
              items={assumptions}
              emptyLabel="No explicit assumptions detected — confirm during workshops."
            />
          </div>
        </div>
      )}
    </section>
  );
}

function RiskOrAssumptionList({
  testId,
  icon: Icon,
  accent,
  title,
  items,
  emptyLabel,
}) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-3.5 h-3.5 ${accent}`} strokeWidth={2} />
        <span className="text-[11px] font-semibold text-[#374151] uppercase tracking-wide">
          {title}
        </span>
        <span className="text-[11px] font-medium text-[#9CA3AF]">
          ({items.length})
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-[12px] text-[#9CA3AF] italic leading-relaxed">
          {emptyLabel}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((t, i) => (
            <li
              key={i}
              className="text-sm text-[#1F2937] leading-relaxed pl-3 border-l-2 border-[#E5E7EB]"
            >
              {t}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
